import { VText } from '@vake/ui-kit';
import { Feature } from 'geojson';
import mapboxgl from 'mapbox-gl';
import { parse, ParsedQuery } from 'query-string';
import { FC, useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useShepherdTour } from 'react-shepherd';
import 'shepherd.js/dist/css/shepherd.css';
import styled from 'styled-components';
import {
    useAvailableDatesQuery,
    useCloudsPerDateQuery,
    useCustomDataQuery,
    useInsightsPerDateQuery,
    useMetadataQuery,
} from '../../../requests/reports';
import RMap from '../../../shared/components/OverwatchMap';
import { DEFAULT_ZOOM_LEVEL, INSIGHT_ZOOM_LEVEL } from '../../../shared/constants';
import { Spinner } from '../../../shared/styles/Spinner';
import { findIndexOfDateInArray, formatLatLonText, getDateISOString, isValidDateString } from '../../../utils/date';
import { useDebounce } from '../../../utils/debounce';
import { ReportType } from '../../../utils/enums';
import { ErrorHandler } from '../../../utils/errorHandling';
import { OverWatchSteps } from '../../../utils/tutorials';
import OverwatchRight from '../components/OverwatchRight';
import { ReportInfo } from './reportInfo/ReportInfo';

const Wrapper = styled.div`
    width: 100vw;
    height: 100vh;
`;

const AbsoluteSpinner = styled(Spinner)`
    position: absolute;
`;

const tourOptions = {
    defaultStepOptions: {
        cancelIcon: {
            enabled: true,
        },
    },
    useModalOverlay: true,
};

const createNewQueryStringWithUpdatedParams = (
    availableDates: Date[],
    index: number,
    searchParams: ParsedQuery<string>,
    detectionConfidence: number,
    selectedFeature: Feature | null
): string => {
    const { date: _, confidence: __, uuid: ___, ...otherQueries } = searchParams;
    const newParams: Record<string, string> = {
        ...otherQueries,
        date: getDateISOString(availableDates[index]),
        confidence: detectionConfidence.toString(),
    };
    const latLon = formatLatLonText(selectedFeature);
    if (latLon) {
        newParams.lat_lon = latLon;
    }
    if (selectedFeature && selectedFeature.properties && selectedFeature.properties.uuid) {
        newParams.uuid = selectedFeature.properties.uuid;
    }

    const newQuery = Object.entries(newParams)
        .map(([key, val]) => `${encodeURIComponent(key)}=${encodeURIComponent(val)}`)
        .join('&');

    return newQuery;
};

const getClampedConfidence = (value: string | null): number | null => {
    if (value === null) return null;

    const parsed = parseFloat(value);

    if (isNaN(parsed)) return null;

    // Return a number between 0 and 100
    return Math.min(100, Math.max(0, parsed));
};

export const InteractiveReport: FC = () => {
    const params = useParams();
    const [searchParams] = useState<ParsedQuery<string>>(parse(useLocation().search));
    const {
        data: availableDates,
        isLoading: isLoadingAvailableDates,
        isError: isErrorAvailableDates,
    } = useAvailableDatesQuery(params, searchParams);
    const [errorShared, setErrorShared] = useState<boolean>(false);
    const [errorSharedMessage, setErrorSharedMessage] = useState<string>('');
    const {
        data: metaData,
        isSuccess: isMetadataSuccess,
        isLoading: isLoadingMetadata,
        isError: isMetadataError,
    } = useMetadataQuery(params, ReportType.InteractiveReport, searchParams);
    const [showProcessingStatus, setProcessingStatus] = useState<boolean>(false);
    const [activeDate, setActiveDate] = useState<Date | undefined>(undefined);
    const [index, setIndex] = useState<number>(-1);
    const defaultConfidence = 92;
    const [detectionConfidence, setDetectionConfidence] = useState<number>(defaultConfidence);
    const [map, setMap] = useState(undefined);
    const [visibleLayers, setVisibleLayers] = useState<string[]>([
        'aoi',
        'custom_data',
        'footprints',
        'cloudmasks',
        'static_objects',
        'undetected_ais',
        'dark_vessels',
        'matched_vessels',
        'connected_vessels',
        'connected_vessels_arrow',
    ]);
    const [selectedFeature, setSelectedFeature] = useState<Feature | null>(null);
    const [hoveredFeature, setHoveredFeature] = useState<Feature | null>(null);
    const popupRef = useRef(new mapboxgl.Popup({ offset: 15 }));
    const [centerPosition, setCenterPosition] = useState<[number, number] | null>(null);
    const [initalZoom, setInitialZoom] = useState<number>(DEFAULT_ZOOM_LEVEL);
    const tour = useShepherdTour({ tourOptions, steps: OverWatchSteps });
    const [insightUuid, setInsightUuid] = useState<string | null>(null);
    const [url, setUrl] = useState<string>(window.location.href);
    const {
        data: clouds,
        isLoading: isLoadingClouds,
        isError: isErrorClouds,
    } = useCloudsPerDateQuery(params, availableDates![index], searchParams);
    const {
        data: insights,
        isLoading: isInsightsLoading,
        isError: isInsightsError,
    } = useInsightsPerDateQuery(
        params,
        availableDates![index], // runs when available dates and valid index is fetched
        showProcessingStatus,
        searchParams
    );
    const { data: customData, isLoading: isLoadingCustomData, isError: isErrorCustomData } = useCustomDataQuery(params);

    useEffect(() => {
        setErrorShared(
            () => isErrorAvailableDates || isMetadataError || isErrorClouds || isInsightsError || isErrorCustomData
        );
        if (isErrorAvailableDates) {
            setErrorSharedMessage('It was an error getting the available dates');
        } else if (isMetadataError) {
            setErrorSharedMessage('It was an error getting the metadata for this report');
        } else if (isErrorClouds) {
            setErrorSharedMessage('It was an error getting the clouds for this date');
        } else if (isInsightsError) {
            setErrorSharedMessage('It was an error getting the insights for this date');
        } else if (isErrorCustomData) {
            setErrorSharedMessage('It was an error getting the custom data for this date');
        } else {
            setErrorSharedMessage("Don't know what went wrong");
        }
    }, [isErrorAvailableDates, isMetadataError, isErrorClouds, isInsightsError, isErrorCustomData]);

    // set page tab title
    useEffect(() => {
        let title = 'Overwatch';
        if (metaData && metaData.metadata && metaData.metadata.name) {
            title = metaData.metadata.name;
            if (!centerPosition) {
                setCenterPosition(metaData.metadata.center_pos);
            }
        }
        document.title = title;
    }, [centerPosition, isMetadataSuccess, metaData]);

    // index is set to the latest known position when available dates are initially loaded
    // Also handles change of date triggered by URL param
    useEffect(() => {
        if (index === -1 && availableDates)
            if (typeof searchParams.date === 'string' && isValidDateString(searchParams.date) && availableDates) {
                const closestDateIndex = findIndexOfDateInArray(availableDates, new Date(searchParams.date));
                setIndex(closestDateIndex);
            } else setIndex(availableDates.length - 1);
    }, [availableDates, index, searchParams]);

    // Handles change of confidence triggered by URL param
    useEffect(() => {
        if (searchParams.confidence && typeof searchParams.confidence === 'string') {
            const clamped = getClampedConfidence(searchParams.confidence);
            if (clamped !== null) {
                setDetectionConfidence(clamped);
            }
        }
        if (searchParams.lat_lon && typeof searchParams.lat_lon === 'string') {
            const lat_lon = searchParams.lat_lon;
            if (lat_lon !== null) {
                const ll = lat_lon.split(',');
                const lon = Number(ll[0]);
                const lat = Number(ll[1]);
                if (typeof lat === 'number' && typeof lon === 'number') {
                    setCenterPosition([lat, lon]);
                    setInitialZoom(INSIGHT_ZOOM_LEVEL);
                }
            }
        }
        if (searchParams.uuid && typeof searchParams.uuid === 'string') {
            setInsightUuid(searchParams.uuid);
        }
    }, [searchParams]);

    useEffect(() => {
        if (index !== -1 && availableDates) {
            setActiveDate(availableDates[index]);
        }
    }, [availableDates, index]);

    const changeUrlParams = useDebounce(
        (
            availableDates: Date[],
            index: number,
            searchParams: ParsedQuery<string>,
            detectionConfidence: number,
            selectedFeature: Feature | null
        ) => {
            const url = window.location.origin + window.location.pathname + '?';
            const newQuery = createNewQueryStringWithUpdatedParams(
                availableDates,
                index,
                searchParams,
                detectionConfidence,
                selectedFeature
            );
            const newUrl = url + newQuery;
            window.history.replaceState({}, '', newUrl);
            setUrl(newUrl);
        },
        300
    ); // wait 300ms after changes

    useEffect(() => {
        if (index !== -1 && availableDates) {
            changeUrlParams(availableDates, index, searchParams, detectionConfidence, selectedFeature);
        }
    }, [availableDates, index, searchParams, detectionConfidence, changeUrlParams, selectedFeature]);

    useEffect(() => {
        if (insights && insightUuid) {
            // Opens a popup for the selected feature if the uuid exist in the url
            const getInsightFeature = (source: string) => {
                return source in insights &&
                    insights[source as keyof Insights] &&
                    (insights[source as keyof Insights] as FeatureCollection).type === 'FeatureCollection'
                    ? (insights[source as keyof Insights] as FeatureCollection).features?.find(
                          (feature: Feature) => feature.properties?.uuid === insightUuid
                      )
                    : undefined;
            };
            const darkVesselsFeature = getInsightFeature('dark_vessels');
            if (darkVesselsFeature) {
                setSelectedFeature(darkVesselsFeature);
            }
            const matchedVesselsFeature = getInsightFeature('matched_vessels');
            if (matchedVesselsFeature) {
                setSelectedFeature(matchedVesselsFeature);
            }
            const undetectedAisFeature = getInsightFeature('undetected_ais');
            if (undetectedAisFeature) {
                setSelectedFeature(undetectedAisFeature);
            }
        }
    }, [insights, insightUuid]);

    if (isLoadingAvailableDates || isLoadingMetadata || isLoadingClouds || isLoadingCustomData) {
        return (
            <Wrapper>
                <AbsoluteSpinner />
            </Wrapper>
        );
    }
    return (
        <Wrapper>
            <ErrorHandler isError={errorShared} errorMessage={errorSharedMessage}>
                <VText variant="H3">Overwatch failed</VText>
                <VText>Something went wrong on our end</VText>
            </ErrorHandler>
            {isInsightsLoading && <AbsoluteSpinner />}
            <ReportInfo
                index={index}
                setIndex={setIndex}
                currMap={map}
                availableDates={availableDates!}
                insights={insights}
                popupRef={popupRef}
                visibleLayers={visibleLayers}
                setVisibleLayers={setVisibleLayers}
                tour={tour}
                detectionConfidence={detectionConfidence}
                setDetectionConfidence={setDetectionConfidence}
                setSelectedFeature={setSelectedFeature}
                setProcessingStatus={setProcessingStatus}
            />
            <OverwatchRight
                aisPositions={visibleLayers.includes('undetected_ais') ? insights?.undetected_ais : undefined}
                matchedVessels={visibleLayers.includes('matched_vessels') ? insights?.matched_vessels : undefined}
                darkVessels={visibleLayers.includes('dark_vessels') ? insights?.dark_vessels : undefined}
                staticObjects={visibleLayers.includes('static_objects') ? insights?.static_objects : undefined}
                selectedFeature={selectedFeature}
                setSelectedFeature={setSelectedFeature}
                setHoveredFeature={setHoveredFeature}
                shipName={null}
                detectionConfidence={detectionConfidence}
                url={url}
            />
            <RMap
                insights={insights}
                clouds={clouds}
                customData={customData}
                metaData={metaData}
                centerPosition={centerPosition}
                initalZoom={initalZoom}
                activeDate={activeDate}
                availableDates={availableDates}
                isInsightsLoading={isInsightsLoading}
                map={map}
                popupRef={popupRef}
                setMap={setMap}
                visibleLayers={visibleLayers}
                detectionConfidence={detectionConfidence}
                selectedFeature={selectedFeature}
                setSelectedFeature={setSelectedFeature}
                hoveredFeature={hoveredFeature}
                setHoveredFeature={setHoveredFeature}
            />
        </Wrapper>
    );
};
